<script setup lang="ts" generic="T extends MenuItemProps">
import type { MenuProps, MenuItemProps, MenuSlots } from '.';
import type { ButtonProps } from 'components/Button';

import { computed, useSlots, useHost } from 'vue';
import { useSharedState } from 'composables/useSharedState';
import { useLayout } from 'components/Layout/useLayout';
import { menuClasses } from './menu.styles';

const props = withDefaults(defineProps<MenuProps<MenuItemProps>>(), {
  logo: true,
  externalIcon: 'export-outline',
  collapsible: true,
  open: false
});

const emit = defineEmits<(event: 'update:open', value: boolean) => void>();

defineSlots<MenuSlots>();

const state = useSharedState<MenuProps<MenuItemProps>>(props);

const slots = useSlots();
const host = useHost();

const { toggleCollapsed, collapsed } = useLayout();

// Check for slotted content in custom elements
const hasLeadingSlot = computed(() => {
  if (host) {
    return host.querySelectorAll('[slot="leading"]').length > 0;
  }
  return !!slots.leading;
});

const hasTrailingSlot = computed(() => {
  if (host) {
    return host.querySelectorAll('[slot="trailing"]').length > 0;
  }
  return !!slots.trailing;
});

const openProxy = computed({
  get: () => state.open?.value ?? false,
  set: (value: boolean) => {
    if (state.open) state.open.value = value;
    emit('update:open', value);
  }
});

const toggleCollapse = () => {
  toggleCollapsed();
};

const toggleOpen = () => {
  openProxy.value = !openProxy.value;
};

const menuStyles = computed(() =>
  menuClasses({
    orientation: 'vertical',
    collapsed: collapsed.value ? 'true' : 'false',
    open: openProxy.value ? 'true' : 'false'
  })
);
</script>

<template>
  <nav
    :class="menuStyles.base"
    class="e-menu-root"
    role="navigation"
    data-orientation="vertical"
    :data-collapsed="collapsed"
    :data-open="openProxy"
  >
    <div
      :class="menuStyles.menu"
      class="e-menu"
    >
      <div
        :class="menuStyles.header"
        class="e-menu-header"
        :data-collapsed="collapsed"
      >
        <slot name="header">
          <e-link-base to="/">
            <e-logo
              v-if="state.logo"
              :class="menuStyles.logo"
              class="e-menu-logo"
              v-bind="state.logo"
              :icon="collapsed"
            ></e-logo>
          </e-link-base>

          <div
            :class="menuStyles.headerOptions"
            class="e-menu-header-options"
          >
            <e-appearance variant="ghost"></e-appearance>
            <e-button
              variant="ghost"
              :icon="openProxy ? 'close' : 'menu'"
              @click="toggleOpen"
            >
            </e-button>
          </div>
        </slot>
      </div>
      <div
        v-if="hasLeadingSlot"
        :class="menuStyles.leading"
        class="e-menu-leading"
      >
        <slot name="leading" />
      </div>
      <ul
        :class="menuStyles.list"
        class="e-menu-list"
      >
        <slot>
          <e-menu-item
            v-for="(item, index) in items"
            :key="index"
            v-bind="item"
          />
        </slot>
      </ul>
      <div
        v-if="hasTrailingSlot"
        :class="menuStyles.trailing"
        class="e-menu-trailing"
      >
        <slot name="trailing" />
      </div>
    </div>

    <div
      :class="menuStyles.footerWrapper"
      class="e-menu-footer-wrapper"
    >
      <div
        :class="menuStyles.footer"
        class="e-menu-footer"
      >
        <slot name="footer">
          <e-appearance variant="ghost"></e-appearance>
        </slot>
      </div>

      <e-button
        v-if="state.collapsible?.value"
        :class="menuStyles.collapsibleButton"
        class="e-menu-footer-collapsible-button"
        variant="ghost"
        icon="collapse"
        v-bind="
          typeof state.collapsible.value === 'object' && (state.collapsible.value as ButtonProps)
        "
        @click="toggleCollapse"
      >
      </e-button>
    </div>

    <e-link-base
      v-if="state.collapsible?.value"
      as="button"
      type="button"
      :class="menuStyles.backgroundButton"
      class="e-menu-background-button"
      @click="toggleCollapse"
    ></e-link-base>
  </nav>
</template>
